import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_ENDPOINTS } from '../../config/apiConfig';
import ConfirmationModal from '../../shared/components/modals/ConfirmationModal';
import ModalCloseButton from '../../shared/components/modals/ModalCloseButton';
import ActionMenu from '../../shared/components/common/ActionMenu';
import './DepartmentManagement.css';

function DepartmentManagement({ token }) {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [formData, setFormData] = useState({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [departmentToDelete, setDepartmentToDelete] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteBlockedInfo, setDeleteBlockedInfo] = useState(null);
  const [blockedTab, setBlockedTab] = useState('devices'); // 'devices' | 'users'
  const [createFormData, setCreateFormData] = useState({
    name: '',
    code: '',
    description: '',
    icon: '🏢'
  });
  const [codeManuallyEdited, setCodeManuallyEdited] = useState(false);
  const [createError, setCreateError] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchDepartments();
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (editingDepartment || showCreateModal || showDeleteConfirmation || deleteBlockedInfo) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [editingDepartment, showCreateModal, showDeleteConfirmation, deleteBlockedInfo]);

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.DEPARTMENTS.LIST, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setDepartments(response.data.departments || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching departments:', error);
      toast.error('Failed to fetch departments');
      setLoading(false);
    }
  };

  const handleEdit = (department) => {
    setEditingDepartment(department);
    setFormData({
      name: department.name,
      code: department.code,
      description: department.description || '',
      icon: department.icon || '🏢'
    });
  };

  const handleCancelEdit = () => {
    setEditingDepartment(null);
    setFormData({});
  };

  const handleDeleteClick = (department) => {
    setDepartmentToDelete(department);
    setShowDeleteConfirmation(true);
  };

  const handleCancelDelete = () => {
    if (isDeleting) return;
    setShowDeleteConfirmation(false);
    setDepartmentToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!departmentToDelete) return;
    setIsDeleting(true);
    try {
      await axios.delete(API_ENDPOINTS.DEPARTMENTS.DELETE(departmentToDelete.id), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Department deleted successfully!');
      setShowDeleteConfirmation(false);
      setDepartmentToDelete(null);
      fetchDepartments();
    } catch (error) {
      const status = error.response?.status;
      const data = error.response?.data;

      if (status === 400 && (data?.device_count != null || data?.user_count != null)) {
        setShowDeleteConfirmation(false);
        setDeleteBlockedInfo(data);
      } else {
        toast.error(data?.error || 'Failed to delete department');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCloseBlockedInfo = () => {
    setDeleteBlockedInfo(null);
  };

  // Pick a good default tab when blocked info arrives
  useEffect(() => {
    if (!deleteBlockedInfo) return;
    const deviceCount = Number(deleteBlockedInfo.device_count || 0);
    const userCount = Number(deleteBlockedInfo.user_count || 0);
    setBlockedTab(deviceCount > 0 ? 'devices' : (userCount > 0 ? 'users' : 'devices'));
  }, [deleteBlockedInfo]);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      await axios.put(API_ENDPOINTS.DEPARTMENTS.UPDATE(editingDepartment.id), formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });
      toast.success('Department updated successfully!');
      setEditingDepartment(null);
      setFormData({});
      fetchDepartments();
    } catch (error) {
      console.error('Error updating department:', error);
      toast.error(error.response?.data?.error || 'Failed to update department');
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCreateFormChange = (e) => {
    const { name, value } = e.target;

    // If user is editing the code field directly, mark it as manually edited
    if (name === 'code') {
      setCodeManuallyEdited(true);
      setCreateFormData(prev => ({ ...prev, code: value }));
    }
    // If user is editing the name field, auto-generate code (unless manually edited)
    else if (name === 'name') {
      setCreateFormData(prev => {
        let generatedCode = value
          .replace(/[^a-zA-Z]/g, '')  // Only letters, no numbers (for departments we'll use 3-5 letters)
          .substring(0, 3);  // First 3 letters only

        // Make code unique by checking against existing departments
        if (generatedCode && !codeManuallyEdited) {
          const existingCodes = departments.map(dept => dept.code);

          // If generated code already exists, append letters until unique
          let counter = 0;
          let uniqueCode = generatedCode;
          while (existingCodes.includes(uniqueCode) && counter < 26) {
            // Try appending next letters: first extend to 4 letters, then 5
            if (uniqueCode.length < 5) {
              const charCode = 65 + counter; // A=65 in ASCII
              uniqueCode = generatedCode + String.fromCharCode(charCode);
            } else {
              // If already 5 letters, replace last letter
              const charCode = 65 + counter;
              uniqueCode = generatedCode.substring(0, 4) + String.fromCharCode(charCode);
            }
            counter++;
          }
          generatedCode = uniqueCode;
        }

        return {
          ...prev,
          name: value,
          // Auto-generate code only if user hasn't manually edited it yet
          code: !codeManuallyEdited ? generatedCode : prev.code
        };
      });
    }
    // For other fields (description, icon)
    else {
      setCreateFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const validateCreateForm = () => {
    if (!createFormData.name || !createFormData.code) {
      setCreateError('Please fill in all required fields');
      return false;
    }
    if (!/^[a-zA-Z]{3,5}$/.test(createFormData.code)) {
      setCreateError('Department code must be 3-5 letters (e.g., RAD, ICU, CARDIO)');
      return false;
    }
    setCreateError('');
    return true;
  };

  const handleCreateDepartment = async (e) => {
    e.preventDefault();
    if (!validateCreateForm()) return;

    setCreateError('');
    setIsCreating(true);

    try {
      await axios.post(API_ENDPOINTS.DEPARTMENTS.CREATE, createFormData, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      toast.success(`Department "${createFormData.name}" created successfully!`);
      setShowCreateModal(false);
      setCreateFormData({
        name: '',
        code: '',
        description: '',
        icon: '🏢'
      });
      setCodeManuallyEdited(false);
      fetchDepartments();
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'An unexpected error occurred while creating the department';
      setCreateError(errorMessage);
    } finally {
      setIsCreating(false);
    }
  };

  const handleOpenCreate = () => {
    setShowCreateModal(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelCreate = () => {
    setShowCreateModal(false);
    setCreateError('');
    setCreateFormData({
      name: '',
      code: '',
      description: '',
      icon: '🏢'
    });
    setCodeManuallyEdited(false);
  };

  const getFilteredDepartments = () => {
    if (!searchTerm) return departments;
    const searchLower = searchTerm.toLowerCase();
    return departments.filter(dept =>
      dept.name.toLowerCase().includes(searchLower) ||
      dept.code.toLowerCase().includes(searchLower) ||
      (dept.description && dept.description.toLowerCase().includes(searchLower))
    );
  };

  if (loading) {
    return <div className="loading">Loading departments...</div>;
  }

  return (
    <div className="department-management">
      <div className="departments-description-section">
        <h1>🏬 Department Management</h1>
        <p>Manage hospital departments and units</p>
      </div>

      <div className="department-toolbar">
        <div className="department-summary" aria-live="polite">
          <span className="summary-count">
            {searchTerm ? getFilteredDepartments().length : departments.length}
          </span>
          <span className="summary-label">
            {searchTerm ? 'Results' : 'Total'}
          </span>
        </div>
        <div className="action-palette">
          <button type="button" className="action-btn action-primary" onClick={handleOpenCreate}>
            <span className="action-icon">➕</span>
            <span className="action-text">Add New Department</span>
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="search-section">
        <div className="search-box-wrapper">
          <span className="search-icon-lens">🔍</span>
          <input
            type="text"
            placeholder="Search by department name, code, or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input-clean"
          />
          {searchTerm && (
            <button
              className="clear-search-btn-inline"
              onClick={() => setSearchTerm('')}
              title="Clear search"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Departments Grid */}
      <div className="departments-grid">
        {getFilteredDepartments().length > 0 ? (
          getFilteredDepartments().map((department) => (
            <div
              key={department.id}
              className="department-card"
            >
              <div className="department-card-header">
                <div className="department-icon-section">
                  <span className="department-icon">{department.icon}</span>
                </div>
                <div className="department-name-section">
                  <h3>{department.name}</h3>
                  <span className="department-code-badge">{department.code}</span>
                </div>
                <ActionMenu
                  ariaLabel={`Actions for ${department.name}`}
                  actions={[
                    {
                      id: 'edit',
                      icon: '✏️',
                      label: 'Edit',
                      onClick: () => handleEdit(department),
                    },
                    {
                      id: 'delete',
                      icon: '🗑️',
                      label: 'Delete',
                      danger: true,
                      onClick: () => handleDeleteClick(department),
                    },
                  ]}
                />
              </div>

              {department.description && (
                <div className="department-description">
                  <p>{department.description}</p>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="empty-state">
            <p>
              {searchTerm
                ? 'No departments found matching your search.'
                : 'No departments found. Create your first department to get started!'}
            </p>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editingDepartment && ReactDOM.createPortal(
        <div className="dept-modal-overlay">
          <div className="dept-modal">
            <div className="dept-modal-header">
              <h3>Edit Department</h3>
              <ModalCloseButton onClick={handleCancelEdit} title="Close" />
            </div>
            <form onSubmit={handleSave}>
              <div className="dept-modal-body">
                <div className="dept-form-grid">
                  <div className="dept-form-row-2">
                    <div className="dept-form-group">
                      <label htmlFor="dept-edit-name">Name *</label>
                      <input
                        id="dept-edit-name"
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleFormChange}
                        required
                        autoFocus
                      />
                    </div>
                    <div className="dept-form-group">
                      <label htmlFor="dept-edit-code">Code *</label>
                      <input
                        id="dept-edit-code"
                        type="text"
                        name="code"
                        value={formData.code}
                        onChange={handleFormChange}
                        required
                        maxLength="5"
                        placeholder="e.g. RAD"
                        title="Department code must be 3-5 letters (case-insensitive)"
                      />
                      <small>3–5 letters</small>
                    </div>
                  </div>

                  <div className="dept-form-group">
                    <label htmlFor="dept-edit-icon">Icon</label>
                    <input
                      id="dept-edit-icon"
                      type="text"
                      name="icon"
                      value={formData.icon}
                      onChange={handleFormChange}
                      maxLength="2"
                      placeholder="e.g. 🏥"
                    />
                  </div>

                  <div className="dept-form-group">
                    <label htmlFor="dept-edit-description">Description</label>
                    <textarea
                      id="dept-edit-description"
                      name="description"
                      value={formData.description}
                      onChange={handleFormChange}
                      rows="2"
                      placeholder="Brief description of the department"
                    />
                  </div>
                </div>
              </div>
              <div className="dept-modal-footer">
                <button type="button" className="action-btn action-secondary" onClick={handleCancelEdit}>
                  Cancel
                </button>
                <button type="submit" className="action-btn action-primary">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Create Modal */}
      {showCreateModal && ReactDOM.createPortal(
        <div className="dept-modal-overlay">
          <div className="dept-modal">
            <div className="dept-modal-header">
              <h3>Add Department</h3>
              <ModalCloseButton onClick={handleCancelCreate} title="Close" />
            </div>
            <form onSubmit={handleCreateDepartment}>
              <div className="dept-modal-body">
                {createError && <div className="dept-modal-error">{createError}</div>}
                <div className="dept-form-grid">
                  <div className="dept-form-row-2">
                    <div className="dept-form-group">
                      <label htmlFor="dept-create-name">Name *</label>
                      <input
                        id="dept-create-name"
                        type="text"
                        name="name"
                        value={createFormData.name}
                        onChange={handleCreateFormChange}
                        required
                        placeholder="e.g. Radiology, ICU"
                        autoFocus
                      />
                    </div>
                    <div className="dept-form-group">
                      <label htmlFor="dept-create-code">Code *</label>
                      <input
                        id="dept-create-code"
                        type="text"
                        name="code"
                        value={createFormData.code}
                        onChange={handleCreateFormChange}
                        required
                        maxLength="5"
                        placeholder="Auto or custom"
                        pattern="[a-zA-Z]{3,5}"
                        title="Department code must be 3-5 letters (case-insensitive)"
                      />
                      <small>
                        {!codeManuallyEdited && createFormData.code
                          ? 'Auto-generated from name — edit to customize'
                          : '3–5 letters'}
                      </small>
                    </div>
                  </div>

                  <div className="dept-form-group">
                    <label htmlFor="dept-create-icon">Icon</label>
                    <input
                      id="dept-create-icon"
                      type="text"
                      name="icon"
                      value={createFormData.icon}
                      onChange={handleCreateFormChange}
                      placeholder="e.g. 🏥"
                      maxLength="2"
                    />
                    <small>Optional single emoji</small>
                  </div>

                  <div className="dept-form-group">
                    <label htmlFor="dept-create-description">Description</label>
                    <textarea
                      id="dept-create-description"
                      name="description"
                      value={createFormData.description}
                      onChange={handleCreateFormChange}
                      placeholder="Brief description of the department"
                      rows="2"
                    />
                  </div>
                </div>
              </div>
              <div className="dept-modal-footer">
                <button type="button" className="action-btn action-secondary" onClick={handleCancelCreate} disabled={isCreating}>
                  Cancel
                </button>
                <button type="submit" className="action-btn action-primary" disabled={isCreating}>
                  {isCreating ? 'Creating...' : 'Create Department'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        entityName={departmentToDelete ? `${departmentToDelete.name} (${departmentToDelete.code})` : 'this department'}
        entityType="department"
        action="delete"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isLoading={isDeleting}
        customMessage="You can delete this department only if it is not currently assigned to any devices or users."
      />

      {/* Delete Blocked Modal (In Use) */}
      {deleteBlockedInfo && ReactDOM.createPortal(
        <div className="dept-modal-overlay">
          <div className="dept-modal dept-modal-wide">
            <div className="dept-modal-header">
              <h3>Cannot Delete Department</h3>
              <ModalCloseButton onClick={handleCloseBlockedInfo} title="Close" />
            </div>
            <div className="dept-modal-body">
              <div className="dept-modal-error">
                {deleteBlockedInfo.department_name
                  ? `“${deleteBlockedInfo.department_name}” cannot be deleted because it is in use.`
                  : `This department cannot be deleted because it is in use.`}{' '}
                {(deleteBlockedInfo.user_count != null) ? `Users: ${deleteBlockedInfo.user_count}.` : ''}{' '}
                {(deleteBlockedInfo.device_count != null) ? `Devices: ${deleteBlockedInfo.device_count}.` : ''}
              </div>

              {(() => {
                const deviceCount = Number(deleteBlockedInfo.device_count || 0);
                const userCount = Number(deleteBlockedInfo.user_count || 0);
                const hasDevices = deviceCount > 0;
                const hasUsers = userCount > 0;
                // Always show tabbed layout for consistency (disable empty tabs).
                const showTabs = (hasDevices || hasUsers);

                const tabBtnStyle = (active) => ({
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: active ? '2px solid var(--dp-accent-primary)' : '2px solid #e9ecef',
                  background: active ? 'var(--dp-accent-focus-ring)' : '#fff',
                  color: active ? 'var(--dp-accent-primary)' : '#495057',
                  fontWeight: 700,
                  cursor: 'pointer'
                });

                return (
                  <>
                    {showTabs && (
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                        <button
                          type="button"
                          onClick={() => setBlockedTab('devices')}
                          style={{
                            ...tabBtnStyle(blockedTab === 'devices'),
                            opacity: hasDevices ? 1 : 0.55,
                            cursor: hasDevices ? 'pointer' : 'not-allowed'
                          }}
                          disabled={!hasDevices}
                        >
                          Devices ({deviceCount})
                        </button>
                        <button
                          type="button"
                          onClick={() => setBlockedTab('users')}
                          style={{
                            ...tabBtnStyle(blockedTab === 'users'),
                            opacity: hasUsers ? 1 : 0.55,
                            cursor: hasUsers ? 'pointer' : 'not-allowed'
                          }}
                          disabled={!hasUsers}
                        >
                          Users ({userCount})
                        </button>
                      </div>
                    )}

                    {/* Devices tab */}
                    {blockedTab === 'devices' && (
                      <>
                        {hasDevices ? (
                          <>
                            <div style={{ marginBottom: '8px', color: '#666' }}>
                              {deleteBlockedInfo.devices_truncated
                                ? `Showing ${deleteBlockedInfo.devices_returned} of ${deviceCount} devices.`
                                : null}
                            </div>
                            {Array.isArray(deleteBlockedInfo.devices) && deleteBlockedInfo.devices.length > 0 ? (
                              <div style={{ maxHeight: '420px', overflow: 'auto', border: '1px solid #eee', borderRadius: '8px' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr style={{ background: '#fafafa' }}>
                                      <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #eee' }}>Identifier</th>
                                      <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #eee' }}>Location</th>
                                      <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #eee' }}>Device Name</th>
                                      <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #eee' }}>Model</th>
                                      <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #eee' }}>Serial</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {deleteBlockedInfo.devices.map((d) => (
                                      <tr key={d.id}>
                                        <td style={{ padding: '10px', borderBottom: '1px solid #f1f1f1', fontWeight: 600 }}>
                                          {d.device_identifier || '-'}
                                        </td>
                                        <td style={{ padding: '10px', borderBottom: '1px solid #f1f1f1' }}>
                                          {d.location_name || '-'}
                                        </td>
                                        <td style={{ padding: '10px', borderBottom: '1px solid #f1f1f1' }}>
                                          {d.device_name || '-'}
                                        </td>
                                        <td style={{ padding: '10px', borderBottom: '1px solid #f1f1f1' }}>
                                          {d.model || '-'}
                                        </td>
                                        <td style={{ padding: '10px', borderBottom: '1px solid #f1f1f1' }}>
                                          {d.serial_number || '-'}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <p style={{ color: '#666' }}>No device details were returned.</p>
                            )}
                          </>
                        ) : (
                          <p style={{ color: '#666' }}>No devices are assigned to this department.</p>
                        )}
                      </>
                    )}

                    {/* Users tab */}
                    {blockedTab === 'users' && (
                      <>
                        {hasUsers ? (
                          <>
                            <div style={{ marginBottom: '8px', color: '#666' }}>
                              {deleteBlockedInfo.users_truncated
                                ? `Showing ${deleteBlockedInfo.users_returned} of ${userCount} users.`
                                : null}
                            </div>
                            {Array.isArray(deleteBlockedInfo.users) && deleteBlockedInfo.users.length > 0 ? (
                              <div style={{ maxHeight: '420px', overflow: 'auto', border: '1px solid #eee', borderRadius: '8px' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr style={{ background: '#fafafa' }}>
                                      <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #eee' }}>Employee ID</th>
                                      <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #eee' }}>Name</th>
                                      <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #eee' }}>Role</th>
                                      <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #eee' }}>Email</th>
                                      <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #eee' }}>Active</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {deleteBlockedInfo.users.map((u) => (
                                      <tr key={u.id}>
                                        <td style={{ padding: '10px', borderBottom: '1px solid #f1f1f1', fontWeight: 600 }}>
                                          {u.employee_id || '-'}
                                        </td>
                                        <td style={{ padding: '10px', borderBottom: '1px solid #f1f1f1' }}>
                                          {u.full_name || '-'}
                                        </td>
                                        <td style={{ padding: '10px', borderBottom: '1px solid #f1f1f1' }}>
                                          {u.role || '-'}
                                        </td>
                                        <td style={{ padding: '10px', borderBottom: '1px solid #f1f1f1' }}>
                                          {u.email || '-'}
                                        </td>
                                        <td style={{ padding: '10px', borderBottom: '1px solid #f1f1f1' }}>
                                          {u.is_active ? 'Yes' : 'No'}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <p style={{ color: '#666' }}>No user details were returned.</p>
                            )}
                          </>
                        ) : (
                          <p style={{ color: '#666' }}>No users are assigned to this department.</p>
                        )}
                      </>
                    )}
                  </>
                );
              })()}
            </div>
            <div className="dept-modal-footer">
              <button type="button" className="action-btn action-primary" onClick={handleCloseBlockedInfo}>
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default DepartmentManagement;
